import { useState, useEffect } from 'react';

/**
 * Custom hook to manage a medical quiz logic.
 * @param {Array} questions - The list of questions for the quiz.
 * @returns An object containing state and functions for controlling quiz behavior.
 */
function useMedicalQuizLogic(questions = []) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [page, setPage] = useState(0);
  
  // Questions per page - could be configurable
  const questionsPerPage = 7;
  
  // Get actual questions
  const actualQuestions = questions;
    
  const totalQuestions = actualQuestions.length;
  
  // Calculate pages
  const totalPages = Math.ceil(totalQuestions / questionsPerPage);
  
  // Get only the questions for the current page
  const startIndex = page * questionsPerPage;
  const currentQuestions = actualQuestions.slice(
    startIndex,
    startIndex + questionsPerPage
  );
  
  // Calculate how many questions have been answered
  const answeredCount = selectedAnswers.filter(
    (answer) => answer !== undefined
  ).length;
  
  // Determine if the quiz is complete
  const isQuizComplete = answeredCount === totalQuestions && totalQuestions > 0;

  // Initialize or reset selected answers when questions change
  useEffect(() => {
    // Reset selected answers array with the correct length
    setSelectedAnswers(Array(totalQuestions).fill(undefined));
    setPage(0);
    setCurrentQuestionIndex(0);
  }, [totalQuestions]);

  /**
   * Handle the selection of an answer for a question.
   * @param {number} questionIndex - The index of the question being answered.
   * @param {any} value - The value of the selected answer.
   */
  const handleAnswerSelection = (questionIndex, value) => {
    setSelectedAnswers((prev) => {
      const newAnswers = [...prev];
      newAnswers[questionIndex] = value;
      return newAnswers;
    });
  };

  /**
   * Move to the next page, if available.
   */
  const goToNextPage = () => {
    setPage((prev) => (prev < totalPages - 1 ? prev + 1 : prev));
  };

  /**
   * Move to the previous page, if available.
   */
  const goToPreviousPage = () => {
    setPage((prev) => (prev > 0 ? prev - 1 : prev));
  };

  /**
   * Reset the quiz to initial state.
   */
  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswers(Array(totalQuestions).fill(undefined));
    setPage(0);
  };

  return {
    currentQuestionIndex,
    selectedAnswers,
    handleAnswerSelection,
    goToNextPage,
    goToPreviousPage,
    resetQuiz,
    totalQuestions,
    currentQuestions,
    page,
    questionsPerPage,
    answeredCount,
    isQuizComplete,
  };
}

export default useMedicalQuizLogic;
